import React, { useState, useEffect } from "react";
import { IonPage, IonContent, IonButton, IonText, useIonAlert, IonHeader, IonGrid } from '@ionic/react';
import { useTranslation } from 'react-i18next';
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import AppHeader from "../../../components/AppHeader/AppHeader";
import KeyPad from "../../../components/KeyPad/KeyPad";
import { useAppSelector } from "../../../services/redux/hook";
import { useDispatch } from "../../../services/redux/store";
import { setUser } from "../../../services/redux/slices/user/user";
import * as Sentry from "@sentry/react";

import './EmailVerificationPage.css';
import { ebioroApi, useVerifyEmailTokenMutation } from "../../../services/api/ebioro/ebioro";
import { EmailTokenVerificationParameters } from "../../../types/types";


const EmailVerificationPage: React.FC<any> = () => {

    const { t, i18n } = useTranslation();
    const [counter, setCounter] = React.useState(30);
    const [pins, setPins] = useState(Array<string>(6).fill(''));
    const [auth_token, setAuthToken] = useState<string>();
    const [emailVerifyTokenId, setEmailVerifyTokenId] = useState('');
    const history = useHistory();
    const [present] = useIonAlert();
    const user = useAppSelector((state) => state.user);
    const dispatch = useDispatch();

    const [verifyEmailToken] = useVerifyEmailTokenMutation();

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        setPins(Array<string>(6).fill(''))
                        return
                    }
                }
            ],

        })

    }

    useEffect(() => {
        counter > 0 && setTimeout(() => setCounter(counter - 1), 1000);
    }, [counter]);

    const {
        handleSubmit,
        formState: { errors }
    } = useForm({ defaultValues: { token: '' } });

    const resendOTP = async () => {

        setPins(Array<string>(6).fill(''))

        let email = user.email;
        let locale = user.locale;
        setCounter(30)

        try {

            await dispatch(ebioroApi.endpoints.getEmailVerificationToken.initiate({ email: email, locale: locale }, { forceRefetch: true })).unwrap();

        } catch (err: any) {

            Sentry.captureException(err)
            presentErrorAlert(t('application.uncaught.ERROR'));
            return
        }
    }

    const onSubmit = async () => {

        const inputCombination = pins.map((i) => String(i)).join('')
        let userEmail = user.email;
        let emailToken = inputCombination;
       
        if (userEmail && emailToken) {

            const verifyEmailParameters:EmailTokenVerificationParameters ={
                email:userEmail as string,
                tokenId:emailVerifyTokenId,
                code:inputCombination,
                auth_token:auth_token as string,
            }

            try {

                const response = await verifyEmailToken(verifyEmailParameters).unwrap();

                if (response.message==='verified'){

                    const updatedUser = { ...user };
    
                    updatedUser.is_email_verified = true;
    
                    dispatch(setUser(updatedUser))
    
                    history.push(
                        {
                            pathname:'/signup/phone',
                            state:{
                                detail:{
                                    auth_token:response.auth_token
                                }
                            }
                    });
                }
    
                else {
    
                    const error = {
                        data: t('registration.email.verification.ERROR_INCORRECT_CODE')
                    }
                    presentErrorAlert(error)
                    return;
    
                }
                
            } catch (error:any) {

                presentErrorAlert({ data: error.data.error })
                return;
                
            }

        }
    }


    const handleClick = (value: any) => {

        let tempCode = pins;

        for (var i = 0; i < tempCode.length; i++) {
            if (tempCode[i] == '') {
                tempCode[i] = value
                break;
            }
            else {
                continue
            }
        }

        setPins([...tempCode])
    }

    const handleRemove = () => {
        let tempCode = pins;
        for (let i = tempCode.length - 1; i >= 0; i--) {
            if (tempCode[i] != '') {
                tempCode[i] = '';
                break
            }
            else {
                continue
            }
        }
        setPins([...tempCode])
    }

    const handleChange = (element: any, index: number) => {
        if (isNaN(element.value)) return false;

        setPins([...pins.map((d, idx) => (idx === index ? element.value : d))]);

        if (element.nextSibling) {
            element.nextSibling.focus();
        }
    };

    useEffect(() => {

        let count = 0

        for (let i = 0; i < pins.length; i++) {

            if (pins[i] != '') {
                count += 1
            }
        }

        if (count === 6) {
            onSubmit()
        }
    }, [pins])

    useEffect(() => {

        const location: any = history.location;
        const auth_token = location.state.detail.auth_token as string;
        setAuthToken(auth_token);
        const verifyTokenId = location.state.detail.verifyTokenId;
        setEmailVerifyTokenId(verifyTokenId);

    }, [])

    return (
        <form onSubmit={handleSubmit(onSubmit)} style={{ padding: 18 }}>

            <IonPage>
                <IonHeader className="ion-no-border">

                    <AppHeader
                        title={t('registration.email.verification.HEADER') as string}
                        goBack={true}
                    >
                    </AppHeader>
                </IonHeader>
                <IonContent scrollY={false}>

                    <div className="ion-text-center otp_inputs">
                        <div className="otp-boxes-container">
                            {pins.map((value, index) => (
                                <div
                                    key={index}
                                    className={`otp-box ${value !== '' ? 'otp-box-filled' : ''}`}
                                >
                                    <span className="otp-text">
                                        {value || ''}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <IonText color="medium" className="ion-text-center">
                        <p className="phonePageInfoText ion-text-wrap">{t('registration.email.verification.DESC')} {user.email}</p>
                    </IonText>

                    <div className="ion-text-center">

                        {counter != 0
                            ? <IonButton
                                disabled={true}
                                onClick={() => resendOTP()}
                                color="primary"
                                fill="clear"
                            >
                                {t('registration.email.verification.BTN_RESEND_CODE')}
                            </IonButton>
                            :
                            <IonButton
                                disabled={false}
                                onClick={() => resendOTP()}
                                color="primary"
                                fill="clear"
                            >
                                {t('registration.email.verification.BTN_RESEND_CODE')}
                            </IonButton>
                        }
                        <div> {t('registration.email.verification.BTN_RESEND_CODE_IN')} {counter}s</div>

                    </div>

                    <IonGrid >
                        <KeyPad handleClick={handleClick} handleRemove={handleRemove} compact />
                    </IonGrid>

                </IonContent>

            </IonPage>
        </form>
    )
}

export default EmailVerificationPage